import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import { toast } from '../../toasts';
import GenericDropdown from '../../components/SelectDropdown';
import generatedDebtService from '../../services/generatedDebts.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { Button as EntityButton } from '../../components/EntityList';

function ApplySurcharge({ debtDue, show, onClose, onSaved }) {
	const { t } = useLabels();
	const [surchargeType, setSurchargeType] = useState('');
	const [value, setValue] = useState('');
	const [loading, setLoading] = useState(false);

	const surchargeTypes = [
		{ value: 'percentage', label: t('form.percentage') },
		{ value: 'amount', label: t('field.amount') },
	];

	// Initialize form when debtDue changes or modal opens
	useEffect(() => {
		if (show && debtDue) {
			if (debtDue.surcharge && debtDue.surcharge > 0) {
				// If there's an existing surcharge, set to amount type and load the value
				setSurchargeType('amount');
				setValue(debtDue.surcharge.toString());
			} else {
				// Reset if no surcharge
				setSurchargeType('');
				setValue('');
			}
		}
	}, [show, debtDue]);

	const handleSurchargeTypeChange = (e) => {
		setSurchargeType(e.value);
		// Only reset value if switching away from amount when there was an existing surcharge
		if (e.value !== 'amount' || !debtDue?.surcharge) {
			setValue('');
		}
	};

	const handleApply = async () => {
		if (!surchargeType) {
			toast.error(t('msg.selectSurchargeType'));
			return;
		}
		if (!value || parseFloat(value) <= 0) {
			toast.error(t('msg.invalidValue'));
			return;
		}

		try {
			setLoading(true);
			await generatedDebtService.applySurcharge(debtDue._id, surchargeType, parseFloat(value));
			toast.success(t('msg.surchargeApplied'));
			onSaved();
			onClose();
			setSurchargeType('');
			setValue('');
		} catch (error) {
			toast.error(getError(error));
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setSurchargeType('');
		setValue('');
		onClose();
	};

	return (
		<Modal show={show} onHide={handleClose} size='lg' centered>
			<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2' closeButton>
				<Modal.Title className='tw:text-[15px] tw:font-semibold'>{t('action.applySurcharge')}</Modal.Title>
			</Modal.Header>
			<Modal.Body className='tw:bg-surface tw:px-5 tw:py-5'>
				<Form>
					<Row>
						<Col>
							<Form.Group className="mb-3">
								<Form.Label>{t('entity.due')}</Form.Label>
								<Form.Control
									type="text"
									value={debtDue?.customId || ''}
									disabled
								/>
							</Form.Group>
						</Col>
						<Col>
							<Form.Group className="mb-3">
								<Form.Label>{t('form.originalAmount')}</Form.Label>
								<Form.Control
									type="text"
									value={debtDue?.amount || ''}
									disabled
								/>
							</Form.Group>
						</Col>
					</Row>
					<Row>
						<Col>
							<Form.Group className="mb-3">
								<GenericDropdown
									label={t('form.surchargeType')}
									items={surchargeTypes}
									selected={surchargeType}
									onChange={handleSurchargeTypeChange}
									accountsList={true}
									required
								/>
							</Form.Group>
						</Col>
						<Col>
							<Form.Group className="mb-3">
								<Form.Label>
									{surchargeType === 'percentage' ? t('form.percentage') : t('field.amount')}
								</Form.Label>
								<Form.Control
									type="number"
									step={surchargeType === 'percentage' ? '0.01' : '1'}
									value={value}
									onChange={(e) => setValue(e.target.value)}
									placeholder={surchargeType === 'percentage' ? 'Ej: 10' : 'Ej: 1000'}
									required
								/>
								{surchargeType === 'percentage' && value && (
									<Form.Text className="text-muted">
										{t('msg.calculatedSurcharge')}: {((debtDue?.amount || 0) * parseFloat(value || 0)) / 100}
									</Form.Text>
								)}
							</Form.Group>
						</Col>
					</Row>
				</Form>
			</Modal.Body>
			<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2'>
				<EntityButton onClick={handleClose} disabled={loading}>{t('action.cancel')}</EntityButton>
				<EntityButton variant='primary' onClick={handleApply} disabled={loading || !surchargeType || !value}>
					{t('action.applySurcharge')}
				</EntityButton>
			</Modal.Footer>
		</Modal>
	);
}

export default ApplySurcharge;
